import type NextLink from 'next/link';
import { type ComponentPropsWithRef, type ElementType, type JSX } from 'react';

/* eslint-disable-next-line no-restricted-imports */
import RootButtonBase, {
  type ButtonBaseTypeMap as RootButtonBaseTypeMap,
} from '@mui/material/ButtonBase';
import { type OverrideProps } from '@mui/material/OverridableComponent';
import { type Theme } from '@mui/material/styles';

import { ensureArrayIsNonEmpty } from '@craft/lib';
import { omit } from '@craft/lib/primitives';

import { type QuantitativeSize } from '../../types/sizes';
import { exhaustiveBorderStyle } from '../../util/borders';
import { type Color } from '../../util/colors';
import { interactableStyle } from '../../util/interaction/parsing';
import {
  type InteractablePointerEventProps,
  type InteractableSxProps,
  InteractionSxPropNames,
} from '../../util/interaction/props';
import { Interaction, InteractionIndicator } from '../../util/interaction/types';
import { mergeSx } from '../../util/mui/sx';

import * as types from './types';

type AbstractButtonBasePropToOmit =
  | 'centerRipple'
  | 'classes'
  | 'disabled'
  | 'disableRipple'
  | 'disableTouchRipple'
  | 'focusRipple'
  | 'TouchRippleProps'
  | 'touchRippleRef';

type AbstractButtonBaseParitalProps<E extends ElementType = ButtonBaseDefaultComponent> = {
  readonly component: E;
  readonly outlineColor?: ((t: Theme) => string) | Color;
  readonly outlineThickness?: QuantitativeSize<'px'>;
  readonly target?: string;
  readonly type?: 'button' | 'reset' | 'submit';
} & InteractablePointerEventProps &
  InteractableSxProps;

/**
 * @see {@link import("@mui/material/ButtonBase").ExtendButtonBaseTypeMap}
 */
type AbstractButtonBaseTypeMap<
  P = Record<never, never>,
  E extends ElementType = ButtonBaseDefaultComponent,
> = {
  defaultComponent: E;
  props: AbstractButtonBaseParitalProps<E> &
    Omit<RootButtonBaseTypeMap['props'], AbstractButtonBasePropToOmit> &
    P;
};

export type ButtonBaseDefaultComponent = RootButtonBaseTypeMap['defaultComponent'];

/**
 * Represents the props for the {@link AbstractButtonBase} component, which are a combination of
 * internally defined props and props that are extended directly from MUI.
 */
type AbstractButtonBaseProps<
  E extends ElementType = ButtonBaseDefaultComponent,
  P = Record<never, never>,
> = OverrideProps<AbstractButtonBaseTypeMap<P, E>, E>;

type AbstractButtonBaseComponentType = <
  E extends ElementType = ButtonBaseDefaultComponent,
  P = Record<never, never>,
>(
  props: { readonly ref?: ComponentPropsWithRef<E>['ref'] } & AbstractButtonBaseProps<E, P>,
) => JSX.Element;

/**
 * Defines the CSS property for 'border' that the {@link ButtonBase} should use.  The
 * {@link ButtonBase} needs to rely on outlines instead of borders, to prevent layout shift when
 * the {@link ButtonBase} is in different states and the border width changes.
 *
 * Eventually, this logic will be translated into the more concrete {@link Button} component,
 * once {@link Button} is updated to favor outlines over borders even in the presence of MUI
 * applied variants.
 */
const ButtonBaseBorder = 'none !important';

/**
 * The type coercion to {@link AbstractButtonBaseComponentType} below is required because the
 * implementation's props, {@link AbstractButtonBaseProps}, are produced by MUI's
 * {@link OverrideProps} type, which distributes over the polymorphic type parameter {@link E}
 * through conditional types and MUI's internal 'DistributiveOmit'.  TypeScript cannot relate those
 * distributive conditional types while {@link E} remains uninstantiated, so it is unable to
 * statically prove that the generic implementation satisfies the public signature,
 * {@link AbstractButtonBaseComponentType}, even though the two are equivalent for every concrete
 * {@link E} a caller can provide.  Annotating the const directly fails with errors indicating that
 * two different 'DistributiveOmit<ComponentPropsWithRef<E>, ...>' types are unrelated.
 *
 * The same limitation existed prior to the React 19 migration, when the implementation was wrapped
 * in 'forwardRef' and coerced to the same public signature in the same manner.  The coercion is
 * safe because every call site type-checks against the concrete instantiation of {@link E}.
 */
const AbstractButtonBase = (<
  E extends ElementType = ButtonBaseDefaultComponent,
  P = Record<never, never>,
>({
  isDisabled = false,
  isLoading = false,
  isLocked = false,
  outlineColor = 'action.active',
  outlineThickness = 1,
  ref,
  ...props
}: { readonly ref?: ComponentPropsWithRef<E>['ref'] } & AbstractButtonBaseProps<E, P>) => (
  <RootButtonBase
    {...omit(props, ensureArrayIsNonEmpty(InteractionSxPropNames))}
    disabled={isDisabled}
    disableRipple
    disableTouchRipple
    ref={ref}
    sx={mergeSx(
      t =>
        exhaustiveBorderStyle(t, {
          color: outlineColor,
          thickness: outlineThickness,
          useOutline: true,
        }),
      (t: Theme) =>
        interactableStyle(
          t,
          [
            {
              indicator: [InteractionIndicator.Border, InteractionIndicator.Background],
              interactions: [Interaction.Focus, Interaction.FocusVisible, Interaction.FocusWithin],
            },
            {
              indicator: InteractionIndicator.Background,
              interaction: Interaction.Hover,
            },
          ],
          {
            selectors: {
              [Interaction.FocusVisible]: ['&:focus-visible', '&.Mui-focusVisible'],
            },
          },
        ),
      {
        alignItems: 'unset',
        borderRadius: '4px',
        display: 'flex',
        flexGrow: 0,
        flexShrink: 0,
        justifyContent: 'unset',
        pointerEvents: isLocked || isDisabled || isLoading ? 'none' : 'auto',
      },
      props.sx,
      { border: ButtonBaseBorder },
    )}
  />
)) as AbstractButtonBaseComponentType;

export type ButtonBaseComponent = 'a' | 'button' | typeof NextLink;

type IfLink<E extends ButtonBaseComponent, TTrue, TFalse> = E extends 'a' | typeof NextLink
  ? TTrue
  : TFalse;

export type ButtonBaseHref<E extends ButtonBaseComponent> = E extends 'a' | typeof NextLink
  ? types.LinkHref<E>
  : never;

export type ButtonBaseProps<E extends ButtonBaseComponent> = AbstractButtonBaseProps<
  E,
  {
    readonly href?: ButtonBaseHref<E>;
    readonly shouldOpenInNewTab?: IfLink<E, boolean, never>;
  }
>;

/**
 * An extension of MUI's, {@link RootButtonBase} component that incorporates default styling and
 * behavior consistent with Craft's design system particularly in regard to focus and hover states.
 *
 * The {@link ButtonBase} component should be used as the foundational component for building
 * clickable/navigation elements that do not conform to the more opinionated and styled
 * {@link Button} or {@link Link} components.
 *
 * This component is specifically designed to be polymorphic via the 'component' prop (similarly
 * to MUI's {@link RootButtonBase}) but the polymorphic nature is restricted to only allow the
 * 'button', 'a', and Next.js {@link Link} components to ensure proper typing of props such as
 * 'href' and 'target'.
 *
 * For example, if a clickable card element is needed, the {@link ButtonBase} component can be used
 * to provide the necessary interaction states without imposing additional styles that would need
 * to be overridden if the {@link Button} was used directly.
 *
 * @see https://mui.com/material-ui/api/button-base/
 */
export const ButtonBase = <E extends ButtonBaseComponent>({
  ref,
  ...props
}: { readonly ref?: ComponentPropsWithRef<E>['ref'] } & ButtonBaseProps<E>): JSX.Element => {
  if (props.component === 'button' || !props.href) {
    return (
      <AbstractButtonBase<'button'>
        {...(omit(props, ['href', 'shouldOpenInNewTab']) as AbstractButtonBaseProps<'button'>)}
        component='button'
        ref={ref as ComponentPropsWithRef<'button'>['ref']}
      >
        {props.children}
      </AbstractButtonBase>
    );
  }
  return (
    <AbstractButtonBase<E>
      {...(omit(props, ['href', 'shouldOpenInNewTab']) as AbstractButtonBaseProps<E>)}
      href={types.getLinkHref({ component: props.component, href: props.href })}
      ref={ref}
      rel={props.rel ?? (props.shouldOpenInNewTab ? 'noopener noreferrer' : undefined)}
      target={props.target ?? (props.shouldOpenInNewTab ? '_blank' : undefined)}
    >
      {props.children}
    </AbstractButtonBase>
  );
};
